import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Globe, User, LogOut, LayoutDashboard, ChevronDown, Phone, ShoppingBag } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { contactInfo } from '../mock';

export default function Header() {
  const { lang, toggle, t } = useLang();
  const { user, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const T = {
    home: { en: 'Home', hi: 'होम' },
    pujas: { en: 'Pujas', hi: 'पूजा' },
    shop: { en: 'Shop', hi: 'दुकान' },
    how: { en: 'How it Works', hi: 'कार्यप्रणाली' },
    corner: { en: 'Devotee Corner', hi: 'भक्त कोना' },
    faq: { en: 'FAQs', hi: 'प्रश्न' },
    login: { en: 'Login', hi: 'लॉगिन' },
    user: { en: 'Devotee Login', hi: 'भक्त लॉगिन' },
    admin: { en: 'Admin Login', hi: 'एडमिन लॉगिन' },
    dashboard: { en: 'Dashboard', hi: 'डैशबोर्ड' },
    logout: { en: 'Logout', hi: 'लॉगआउट' }
  };

  const navLinks = [
    { to: '/', label: T.home },
    { to: '/upcoming-pujas', label: T.pujas },
    { to: '/shop', label: T.shop },
    { to: '/#how', label: T.how },
    { to: '/#corner', label: T.corner },
    { to: '/#faq', label: T.faq }
  ];

  const dashPath = user?.role === 'admin' ? '/admin/dashboard' : '/dashboard';

  const handleHash = (e, to) => {
    if (to.startsWith('/#')) {
      e.preventDefault();
      const id = to.slice(2);
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
      setOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 wine-dark border-b border-nv-gold/20 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 md:gap-4 group">
            <img
              src="/brand/logo.png"
              alt="Navavidha Bhakti Logo"
              className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-[0_4px_12px_rgba(233,167,86,0.4)] group-hover:scale-105 group-hover:rotate-3 transition duration-300"
            />
            <div className="leading-tight">
              <div className="text-lg md:text-2xl font-bold tracking-wider gradient-text-on-dark uppercase">
                Navavidha
              </div>
              <div className="text-[10px] md:text-[12px] tracking-[0.35em] text-nv-gold-light/85 uppercase">
                Bhakti
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.to}
                href={l.to}
                onClick={(e) => (l.to.startsWith('/#') ? handleHash(e, l.to) : null)}
                className="nav-link text-sm font-medium text-nv-cream/85 hover:text-nv-gold-light"
              >
                {t(l.label)}
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${contactInfo.phone}`} className="hidden xl:flex items-center gap-2 px-4 py-2 rounded-full bg-nv-gold/15 hover:bg-nv-gold/25 text-nv-gold-light text-sm font-medium transition border border-nv-gold/30">
              <Phone className="w-4 h-4" /> {contactInfo.phone}
            </a>

            <button
              onClick={toggle}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-nv-gold/40 bg-nv-gold/10 hover:bg-nv-gold/20 text-sm font-medium text-nv-gold-light transition"
            >
              <Globe className="w-4 h-4" />
              {lang === 'en' ? 'हि' : 'EN'}
            </button>

            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 rounded-full border border-nv-gold/40 bg-nv-gold/10 hover:bg-nv-gold/20 text-nv-gold-light transition"
              data-testid="cart-open-btn"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="cart-count-badge">
                  {count}
                </span>
              )}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2 border-nv-gold/50 bg-transparent text-nv-cream hover:bg-nv-gold/15">
                    <User className="w-4 h-4" /> {user.name} <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(dashPath)}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t(T.dashboard)}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" /> {t(T.logout)}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => navigate('/login')}
                data-testid="login-btn"
                className="btn-gold px-5 py-2 rounded-full text-sm font-semibold"
              >
                {t(T.login)}
              </button>
            )}
          </div>

          {/* Mobile menu button + cart */}
          <div className="lg:hidden flex items-center gap-1">
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-nv-gold-light"
              data-testid="cart-open-btn-mobile"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-6 h-6" />
              {count > 0 && (
                <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {count}
                </span>
              )}
            </button>
            <button className="p-2 text-nv-gold-light" onClick={() => setOpen(!open)}>
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden pb-4 border-t border-nv-gold/20 mt-2 pt-4 space-y-3">
            {navLinks.map((l) => (
              <a
                key={l.to}
                href={l.to}
                onClick={(e) => { if (l.to.startsWith('/#')) handleHash(e, l.to); else setOpen(false); }}
                className="block text-nv-cream/85 hover:text-nv-gold-light font-medium py-2"
              >
                {t(l.label)}
              </a>
            ))}
            <div className="flex gap-2 flex-wrap pt-3 border-t border-nv-gold/15">
              <button onClick={toggle} className="px-3 py-2 rounded-full border border-nv-gold/40 text-sm text-nv-gold-light">
                {lang === 'en' ? 'हिन्दी' : 'English'}
              </button>
              {user ? (
                <>
                  <button onClick={() => { navigate(dashPath); setOpen(false); }} className="btn-gold px-4 py-2 rounded-full text-sm">{t(T.dashboard)}</button>
                  <button onClick={() => { logout(); navigate('/'); setOpen(false); }} className="px-4 py-2 rounded-full border border-red-400/50 text-red-300 text-sm">{t(T.logout)}</button>
                </>
              ) : (
                <button onClick={() => { navigate('/login'); setOpen(false); }} className="btn-gold px-4 py-2 rounded-full text-sm">{t(T.login)}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
